import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectAdvisorOnboardingChecklistData,
  selectAdvisorOnboardingCompletedStepsData,
  selectAdvisorOnboardingStatus,
  selectClients,
} from '@client/model/selectors';
import {
  fetchAdvisorOnboardingStatusByAdvisorIdRequest,
  putAdvisorOnboardingCompletedStepsByAdvisorIdRequest,
} from '@client/model/advisorOnboardingModel';

export const ONBOARDING_STEPS = [
  'welcome',
  'invite-client',
  'client-intake-form',
  'complete-profile',
] as const;

export const ONBOARDING_CLIENT_THRESHOLD = 5;

export function useAdvisorOnboarding(advisorId: string) {
  const dispatch = useDispatch();
  const status = useSelector(selectAdvisorOnboardingStatus);
  const checklist = useSelector(selectAdvisorOnboardingChecklistData);
  const completedStepsData = useSelector(selectAdvisorOnboardingCompletedStepsData);
  const clients = useSelector(selectClients);

  const clientCount = clients?.length ?? 0;
  const hasCompletedAllSteps =
    completedStepsData?.completedSteps &&
    ONBOARDING_STEPS.every(step => completedStepsData.completedSteps.includes(step));
  const shouldShowOnboarding = clientCount < ONBOARDING_CLIENT_THRESHOLD && !hasCompletedAllSteps;

  const refetch = useCallback(() => {
    if (advisorId) {
      dispatch(fetchAdvisorOnboardingStatusByAdvisorIdRequest({ advisorId }));
    }
  }, [advisorId, dispatch]);

  const updateCompletedSteps = useCallback(
    (completedSteps: string[]) => {
      if (advisorId) {
        dispatch(
          putAdvisorOnboardingCompletedStepsByAdvisorIdRequest({
            advisorId,
            completedSteps,
          }),
        );
      }
    },
    [advisorId, dispatch],
  );

  const markStepAsCompleted = useCallback(
    (stepId: string) => {
      const currentSteps = completedStepsData?.completedSteps ?? [];
      if (!currentSteps.includes(stepId)) {
        updateCompletedSteps([...currentSteps, stepId]);
      }
    },
    [completedStepsData?.completedSteps, updateCompletedSteps],
  );

  const markStepAsIncompleted = useCallback(
    (stepId: string) => {
      const currentSteps = completedStepsData?.completedSteps;

      if (!currentSteps) return;
      if (!currentSteps.includes(stepId)) return;

      updateCompletedSteps(currentSteps.filter((s: string) => s !== stepId));
    },
    [completedStepsData?.completedSteps, updateCompletedSteps],
  );

  const isStepCompleted = useCallback(
    (stepId: string) => {
      return completedStepsData?.completedSteps?.includes(stepId) ?? false;
    },
    [completedStepsData?.completedSteps],
  );

  useEffect(() => {
    if (advisorId && status === null) {
      dispatch(fetchAdvisorOnboardingStatusByAdvisorIdRequest({ advisorId }));
    }
  }, [advisorId, status, dispatch]);

  return {
    checklist,
    completedSteps: completedStepsData?.completedSteps ?? [],
    completedStepsData,
    isLoading: status === 'loading',
    isError: status === 'error',
    isComplete: status === 'complete',
    refetch,
    updateCompletedSteps,
    markStepAsCompleted,
    markStepAsIncompleted,
    isStepCompleted,
    clientCount,
    hasCompletedAllSteps,
    shouldShowOnboarding,
  };
}
